import React, { useState, useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import { usePlanStore } from '../store/usePlanStore';
import { calculateVolumeMetrics } from '../logic/volumeEngine';
import { calculateFatigueMetrics } from '../logic/fatigueSystem';
import type { Plan } from '../types';
import { MUSCLE_GROUP_LABELS } from '../types';

export default function PlanComparison() {
    const savedPlans = usePlanStore((s) => s.savedPlans);
    const [planAId, setPlanAId] = useState<string>('');
    const [planBId, setPlanBId] = useState<string>('');

    const planA = savedPlans.find((p: Plan) => p.id === planAId);
    const planB = savedPlans.find((p: Plan) => p.id === planBId);

    const statsA = useMemo(() => (planA ? getStats(planA) : null), [planA]);
    const statsB = useMemo(() => (planB ? getStats(planB) : null), [planB]);

    return (
        <div className="max-w-4xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-purple-100 dark:bg-purple-950 flex items-center justify-center">
                    <BarChart3 size={22} className="text-purple-600" />
                </div>
                <div>
                    <h2 className="text-lg font-bold">Porównanie planów</h2>
                    <p className="text-xs text-slate-400">
                        Porównaj objętość, częstotliwość i intensywność dwóch planów
                    </p>
                </div>
            </div>

            {/* Plan selectors */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Plan A</label>
                    <select
                        value={planAId}
                        onChange={(e) => setPlanAId(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                    >
                        <option value="">Wybierz plan...</option>
                        {savedPlans.map((p: Plan) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Plan B</label>
                    <select
                        value={planBId}
                        onChange={(e) => setPlanBId(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                    >
                        <option value="">Wybierz plan...</option>
                        {savedPlans.map((p: Plan) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* No plans */}
            {savedPlans.length < 2 && (
                <div className="text-center py-16">
                    <BarChart3 size={32} className="text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Potrzebujesz minimum 2 planów, aby je porównać</p>
                </div>
            )}

            {/* Comparison table */}
            {statsA && statsB && (
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-slate-200 dark:border-slate-700">
                                <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Metryka</th>
                                <th className="text-center px-4 py-3 text-xs font-bold text-brand-600 uppercase">{planA!.name}</th>
                                <th className="text-center px-4 py-3 text-xs font-bold text-purple-600 uppercase">{planB!.name}</th>
                                <th className="text-center px-4 py-3 text-xs font-bold text-slate-400 uppercase">Różnica</th>
                            </tr>
                        </thead>
                        <tbody>
                            <CompareRow label="Sesje / tydzień" a={statsA.sessions} b={statsB.sessions} />
                            <CompareRow label="Ćwiczenia łącznie" a={statsA.exercises} b={statsB.exercises} />
                            <CompareRow label="Serie łącznie" a={statsA.totalSets} b={statsB.totalSets} />
                            <CompareRow label="Zmęczenie (%)" a={statsA.fatigue} b={statsB.fatigue} higher="worse" />
                            <CompareRow label="Unikalne ćwiczenia" a={statsA.uniqueExercises} b={statsB.uniqueExercises} />

                            {/* Muscle volume comparison */}
                            <tr className="bg-slate-50 dark:bg-slate-800/50">
                                <td colSpan={4} className="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                                    Objętość na grupę mięśniową (serie/tydzień)
                                </td>
                            </tr>
                            {getAllMuscles(statsA, statsB).map((muscle) => (
                                <CompareRow
                                    key={muscle}
                                    label={MUSCLE_GROUP_LABELS[muscle as keyof typeof MUSCLE_GROUP_LABELS] || muscle}
                                    a={statsA.muscleVolumes[muscle] || 0}
                                    b={statsB.muscleVolumes[muscle] || 0}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Helpers ──

interface PlanStats {
    sessions: number;
    exercises: number;
    totalSets: number;
    fatigue: number;
    uniqueExercises: number;
    muscleVolumes: Record<string, number>;
}

function getStats(plan: Plan): PlanStats {
    const week = plan.weeks[0];
    if (!week) {
        return { sessions: 0, exercises: 0, totalSets: 0, fatigue: 0, uniqueExercises: 0, muscleVolumes: {} };
    }

    const volume = calculateVolumeMetrics(week, plan.trainingLevel);
    const fatigue = calculateFatigueMetrics(week, plan.fatigueTolerance);

    const exerciseNames = new Set<string>();
    let totalExercises = 0;

    for (const session of week.sessions) {
        for (const ex of session.exercises) {
            exerciseNames.add(ex.name);
            totalExercises++;
        }
    }

    const muscleVolumes: Record<string, number> = {};
    for (const mv of volume.muscleVolumes) {
        muscleVolumes[mv.muscle] = mv.totalSets;
    }

    return {
        sessions: week.sessions.length,
        exercises: totalExercises,
        totalSets: volume.totalSets,
        fatigue: Math.round(fatigue.overallFatigue),
        uniqueExercises: exerciseNames.size,
        muscleVolumes,
    };
}

function getAllMuscles(a: PlanStats, b: PlanStats): string[] {
    const set = new Set([...Object.keys(a.muscleVolumes), ...Object.keys(b.muscleVolumes)]);
    return Array.from(set).sort();
}

function CompareRow({ label, a, b, higher = 'better' }: {
    label: string;
    a: number;
    b: number;
    higher?: 'better' | 'worse';
}) {
    const diff = a - b;
    const diffStr = diff > 0 ? `+${diff}` : `${diff}`;
    const diffColor = diff === 0
        ? 'text-slate-400'
        : (higher === 'better' ? (diff > 0 ? 'text-green-500' : 'text-red-500') : (diff > 0 ? 'text-red-500' : 'text-green-500'));

    return (
        <tr className="border-t border-slate-100 dark:border-slate-800">
            <td className="px-4 py-2 text-xs text-slate-500">{label}</td>
            <td className="px-4 py-2 text-center font-medium">{a}</td>
            <td className="px-4 py-2 text-center font-medium">{b}</td>
            <td className={`px-4 py-2 text-center text-xs font-bold ${diffColor}`}>{diffStr}</td>
        </tr>
    );
}
